import React, { useEffect, useState } from "react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const AddShops = () => {
    const [data, setData] = useState({ shops: [] });
    const [searchTerm, setSearchTerm] = useState("");
    const [newShop, setNewShop] = useState({
        name: "",
        contact: "",
        address: "",
        area: "",
        email: "",
    });
    const [editValues, setEditValues] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState("");

    const [editingShopKey, setEditingShopKey] = useState(null);

    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
    const customerType = sessionStorage.getItem("customerType");

    const fetchShops = async () => {
        try {
            setIsLoading(true);
            setError("");
            const res = await fetch(`${apiBaseUrl}/datashops`);
            if (!res.ok) throw new Error("Failed to fetch shops");
            const result = await res.json();
            setData(result);
        } catch (err) {
            console.error(err);
            setError("Failed to load shops. Please refresh.");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchShops();
    }, [apiBaseUrl]);

    const getShops = () => data?.shops || [];

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setNewShop((prev) => ({ ...prev, [name]: value }));
    };

    const validateFields = (shop) => {
        for (const key in shop) {
            if (key !== "email" && !shop[key]?.trim()) {
                alert(`Please enter ${key.charAt(0).toUpperCase() + key.slice(1)}`);
                return false;
            }
        }
        if (!/^\d{10}$/.test(shop.contact)) {
            alert("Contact number must be exactly 10 digits");
            return false;
        }
        return true;
    };

    const handleAddShop = async () => {
        if (!validateFields(newShop)) return;

        const shops = getShops();
        const trimmedName = newShop.name.trim();
        const trimmedAddress = newShop.address.trim();
        const trimmedArea = newShop.area.trim();

        // Check for exact duplicate (name + address + area)
        const isDuplicate = shops.some(
            (shop) =>
                shop.name.toLowerCase() === trimmedName.toLowerCase() &&
                shop.address.toLowerCase() === trimmedAddress.toLowerCase() &&
                (shop.area || "").toLowerCase() === trimmedArea.toLowerCase()
        );

        if (isDuplicate) {
            alert(
                `A shop named "${trimmedName}" at "${trimmedAddress}" in "${trimmedArea || "this area"}" already exists!`
            );
            return;
        }

        try {
            const res = await fetch(`${apiBaseUrl}/addshop`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    shop: {
                        ...newShop,
                        name: trimmedName,
                        address: trimmedAddress,
                        area: trimmedArea,
                    },
                }),
            });

            if (!res.ok) {
                const err = await res.json();
                throw new Error(err.error || "Failed to add shop");
            }

            setNewShop({ name: "", contact: "", address: "", area: "", email: "" });
            fetchShops();
            alert("Shop added successfully!");
        } catch (err) {
            alert(err.message || "Failed to add shop");
        }
    };

    const handleDeleteShop = async (shop) => {
        if (!window.confirm(`Delete shop "${shop.name}" at "${shop.address}" permanently?`)) return;

        try {
            const res = await fetch(`${apiBaseUrl}/deleteshop`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    shop: { name: shop.name, address: shop.address },
                }),
            });

            if (!res.ok) {
                const errData = await res.json().catch(() => ({}));
                throw new Error(errData.error || `Failed to delete shop (HTTP ${res.status})`);
            }

            fetchShops();
            alert("Shop deleted successfully!");
        } catch (err) {
            console.error("Delete error:", err);
            alert(err.message || "Failed to delete shop. Please try again.");
        }
    };

    const startEdit = (shop) => {
        const uniqueKey = `${shop.name}|||${shop.address}`;
        setEditingShopKey(uniqueKey);
        setEditValues({ ...shop });
    };

    const handleEditChange = (e) => {
        const { name, value } = e.target;
        setEditValues((prev) => ({ ...prev, [name]: value }));
    };

    const handleSaveEdit = async () => {
        if (!validateFields(editValues)) return;

        const trimmedName = editValues.name.trim();
        const trimmedAddress = editValues.address.trim();
        const trimmedArea = (editValues.area || "").trim();
        const trimmedContact = (editValues.contact || "").trim();
        const trimmedEmail = (editValues.email || "").trim();

        const originalShopKey = editingShopKey;
        const [origName, origAddress] = originalShopKey.split("|||");

        const originalShop = getShops().find(
            (s) => s.name === origName && s.address === origAddress
        );

        if (!originalShop) {
            alert("Original shop data not found!");
            return;
        }

        const hasChanges =
            originalShop.name.trim() !== trimmedName ||
            originalShop.address.trim() !== trimmedAddress ||
            (originalShop.area || "").trim() !== trimmedArea ||
            (originalShop.contact || "").trim() !== trimmedContact ||
            (originalShop.email || "").trim() !== trimmedEmail;

        if (!hasChanges) {
            alert("No changes made.");
            handleCancelEdit();
            return;
        }

        // Duplicate check (exclude self)
        const isDuplicate = getShops().some((shop) => {
            const shopKey = `${shop.name}|||${shop.address}`;
            return (
                shopKey !== originalShopKey &&
                shop.name.toLowerCase() === trimmedName.toLowerCase() &&
                shop.address.toLowerCase() === trimmedAddress.toLowerCase() &&
                (shop.area || "").toLowerCase() === trimmedArea.toLowerCase()
            );
        });

        if (isDuplicate) {
            alert("Another shop with this name, address and area already exists!");
            return;
        }

        try {
            const res = await fetch(`${apiBaseUrl}/updateShop`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    oldShop: {
                        name: originalShop.name,
                        address: originalShop.address,
                    },
                    newShop: {
                        name: trimmedName,
                        address: trimmedAddress,
                        area: trimmedArea,
                        contact: trimmedContact,
                        email: trimmedEmail,
                    },
                }),
            });

            if (!res.ok) {
                const err = await res.json();
                throw new Error(err.error || "Update failed");
            }

            handleCancelEdit();
            fetchShops();
            alert("Shop updated successfully!");
        } catch (err) {
            alert(err.message || "Failed to update shop");
        }
    };

    const handleCancelEdit = () => {
        setEditingShopKey(null);
        setEditValues({});
    };

    const filteredShops = getShops().filter((shop) => {
        if (!searchTerm.trim()) return true;

        const term = searchTerm.toLowerCase().trim();

        // Support searching phone numbers regardless of +91, spaces or symbols
        const cleanedSearchTerms = term
            .split(",")
            .map((t) =>
                t
                    .replace(/^\+91|\+91|\+/g, "")
                    .replace(/\s/g, "")
                    .replace(/\D/g, "")
            )
            .filter(Boolean);

        const cleanedContact = (shop.contact || "")
            .replace(/^\+91|\+91|\+/g, "")
            .replace(/\s/g, "")
            .replace(/\D/g, "");

        const matchesMobile =
            cleanedSearchTerms.length > 0
                ? cleanedSearchTerms.some((cleanTerm) => cleanedContact.includes(cleanTerm))
                : shop.contact?.toLowerCase().includes(term);

        return (
            shop.name?.toLowerCase().includes(term) ||
            matchesMobile ||
            shop.address?.toLowerCase().includes(term) ||
            shop.area?.toLowerCase().includes(term) ||
            shop.email?.toLowerCase().includes(term)
        );
    });

    const downloadExcel = () => {
        if (filteredShops.length === 0) {
            alert("No data to export!");
            return;
        }

        const filterWord = searchTerm.trim() === "" ? "All" : searchTerm.trim();
        const safeFilterWord = filterWord.replace(/[^a-zA-Z0-9]/g, "_");

        const worksheetData = filteredShops.map((shop, index) => ({
            "S.No": index + 1,
            "Shop Name": shop.name,
            Contact: shop.contact,
            Address: shop.address || "N/A",
            Area: shop.area || "N/A",
            Email: shop.email || "N/A",
        }));

        const worksheet = XLSX.utils.json_to_sheet(worksheetData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Shops");

        worksheet["!cols"] = [
            { wch: 8 },
            { wch: 30 },
            { wch: 15 },
            { wch: 50 },
            { wch: 20 },
            { wch: 30 },
        ];

        const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
        const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
        const today = new Date().toISOString().slice(0, 10);
        saveAs(blob, `Shops_${safeFilterWord}_${today}.xlsx`);
    };

    if (isLoading) return <p className="text-center py-20 text-gray-600">Loading shops...</p>;
    if (error) return <p className="text-center py-20 text-red-600">{error}</p>;

    return (
        <div className="min-h-screen bg-gray-100 py-8 px-4 sm:px-8">
            <div className="max-w-8xl mx-auto bg-white shadow-md rounded-lg p-6">
                <h1 className="text-2xl font-bold text-center text-gray-800 mb-6">Manage Shops</h1>

                {/* Add Shop Form */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                    {["name", "contact", "address", "area", "email"].map((field) => (
                        <input
                            key={field}
                            name={field}
                            value={newShop[field]}
                            onChange={handleInputChange}
                            placeholder={field.charAt(0).toUpperCase() + field.slice(1)}
                            className="border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            required={field !== "email"}
                        />
                    ))}
                </div>

                <div className="w-full mb-6 flex justify-center gap-4 flex-wrap">
                    <button
                        onClick={handleAddShop}
                        className="bg-blue-500 text-white rounded-lg px-6 py-2 shadow hover:bg-blue-600 transition duration-300"
                    >
                        Add Shop
                    </button>

                    {customerType === "mainAdmin" && (
                        <button
                            onClick={downloadExcel}
                            className="bg-green-600 text-white rounded-lg px-6 py-2 shadow hover:bg-green-700 transition duration-300 flex items-center gap-2"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-5 w-5"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                />
                            </svg>
                            Download Excel
                        </button>
                    )}
                </div>

                {/* Search */}
                <div className="mb-6">
                    <input
                        name="shopSearch"
                        type="search"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Search name, contact, address, area, email..."
                        autoComplete="new-password"
                        autoCorrect="off"
                        autoCapitalize="none"
                        spellCheck={false}
                        onFocus={(e) => e.target.setAttribute("autocomplete", "new-password")}
                        className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                </div>

                {/* Shops Table */}
                <div className="overflow-x-auto">
                    <table className="min-w-full border border-gray-300 rounded-lg">
                        <thead className="bg-gray-200">
                            <tr>
                                {["Name", "Contact", "Address", "Area", "Email", "Actions"].map((header) => (
                                    <th key={header} className="text-center px-4 py-2 border text-gray-700">
                                        {header}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {filteredShops.length > 0 ? (
                                filteredShops.map((shop) => {
                                    const uniqueKey = `${shop.name}|||${shop.address}`;

                                    return (
                                        <tr key={uniqueKey} className="border-t">
                                            {editingShopKey === uniqueKey ? (
                                                // Edit mode
                                                <>
                                                    {["name", "contact", "address", "area", "email"].map((key) => (
                                                        <td key={key} className="px-4 py-2 border">
                                                            <input
                                                                name={key}
                                                                value={editValues[key] || ""}
                                                                onChange={handleEditChange}
                                                                className="w-full border border-gray-300 rounded-lg px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                                                autoFocus={key === "name"}
                                                            />
                                                        </td>
                                                    ))}
                                                    <td className="px-4 py-2 flex gap-2 border justify-center">
                                                        <button
                                                            onClick={handleSaveEdit}
                                                            className="bg-green-500 text-white px-3 py-1 rounded hover:bg-green-600 text-sm"
                                                        >
                                                            Save
                                                        </button>
                                                        <button
                                                            onClick={handleCancelEdit}
                                                            className="bg-gray-500 text-white px-3 py-1 rounded hover:bg-gray-600 text-sm"
                                                        >
                                                            Cancel
                                                        </button>
                                                    </td>
                                                </>
                                            ) : (
                                                // View mode
                                                <>
                                                    <td className="px-4 py-2 border text-center">{shop.name}</td>
                                                    <td className="px-4 py-2 border text-center">{shop.contact}</td>
                                                    <td className="px-4 py-2 border text-center">{shop.address}</td>
                                                    <td className="px-4 py-2 border text-center">
                                                        {shop.area || "—"}
                                                    </td>
                                                    <td className="px-4 py-2 border text-center">
                                                        {shop.email || "N/A"}
                                                    </td>
                                                    <td className="px-4 py-2 border flex justify-center gap-2">
                                                        <button
                                                            onClick={() => startEdit(shop)}
                                                            className="bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-600 text-sm"
                                                        >
                                                            Edit
                                                        </button>
                                                        {customerType === "mainAdmin" && (
                                                            <button
                                                                onClick={() => handleDeleteShop(shop)}
                                                                className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600 text-sm"
                                                            >
                                                                Delete
                                                            </button>
                                                        )}
                                                    </td>
                                                </>
                                            )}
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td colSpan={6} className="text-center text-gray-500 py-4 border">
                                        No shops found
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default AddShops;
